import React, { useState, useEffect, useCallback } from 'react';
import { getErrorMessage } from '../../utils/errorHandler';

interface RealEstateLedgerProps {
    onBack: () => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface RealEstateItem {
    id: number;
    fixed_asset_id: number;
    location: string | null;
    lot_number: string | null;
    land_category_structure: string | null;
    area: number | null;
    registration_date: string | null;
    remarks: string | null;
    asset_code: string;
    asset_name: string;
    acquisition_date: string | null;
}

const RealEstateLedger: React.FC<RealEstateLedgerProps> = ({
    onBack,
    tenantName,
    currentUser,
    apiFetch,
    currentTenantId,
}) => {
    const [items, setItems] = useState<RealEstateItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorStatus, setErrorStatus] = useState<string | null>(null);
    const [successStatus, setSuccessStatus] = useState<string | null>(null);

    // Form State
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<RealEstateItem>>({});
    const [fixedAssets, setFixedAssets] = useState<any[]>([]);

    const fetchFixedAssets = useCallback(async () => {
        if (!currentTenantId) return;
        try {
            const res = await apiFetch(`/api/fixed-assets?page_size=1000`);
            if (res.ok) {
                const data = await res.json();
                setFixedAssets(data.items || []);
            }
        } catch (e) {
            console.error(e);
        }
    }, [apiFetch, currentTenantId]);

    const fetchItems = useCallback(async () => {
        if (!currentTenantId) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/real-estate`);
            if (!res.ok) {
                const msg = await getErrorMessage(res, '不動産台帳データの取得に失敗しました');
                throw new Error(msg);
            }
            const data = await res.json();
            setItems(data);
        } catch (e: any) {
            setErrorStatus(e.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, currentTenantId]);

    useEffect(() => {
        fetchItems();
        fetchFixedAssets();
    }, [fetchItems, fetchFixedAssets]);

    const handleEdit = (item: RealEstateItem) => {
        setIsEditing(true);
        setFormData(item);
        setErrorStatus(null);
        setSuccessStatus(null);
    };

    const handleNew = () => {
        setIsEditing(true);
        setFormData({});
        setErrorStatus(null);
        setSuccessStatus(null);
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setFormData({});
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value === '' ? null : value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.fixed_asset_id) {
            setErrorStatus('紐づける固定資産を選択してください');
            return;
        }

        const isUpdate = !!formData.id;
        const url = isUpdate ? `/api/real-estate/${formData.id}` : `/api/real-estate`;
        const method = isUpdate ? 'PUT' : 'POST';

        const payload = {
            fixed_asset_id: Number(formData.fixed_asset_id),
            location: formData.location || null,
            lot_number: formData.lot_number || null,
            land_category_structure: formData.land_category_structure || null,
            area: formData.area ? Number(formData.area) : null,
            registration_date: formData.registration_date || null,
            remarks: formData.remarks || null,
        };

        try {
            const res = await apiFetch(url, {
                method,
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const msg = await getErrorMessage(res, '保存に失敗しました');
                throw new Error(msg);
            }
            setSuccessStatus('保存しました');
            setErrorStatus(null);
            setIsEditing(false);
            fetchItems();
        } catch (e: any) {
            setErrorStatus(e.message);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('本当に削除しますか？')) return;
        try {
            const res = await apiFetch(`/api/real-estate/${id}`, { method: 'DELETE' });
            if (!res.ok) {
                const msg = await getErrorMessage(res, '削除に失敗しました');
                throw new Error(msg);
            }
            setSuccessStatus('削除しました');
            fetchItems();
        } catch (e: any) {
            setErrorStatus(e.message);
        }
    };

    // Selectable fixed assets (exclude already linked ones, except when editing)
    const availableAssets = fixedAssets.filter((asset: any) => {
        if (formData.fixed_asset_id === asset.id) return true;
        return !items.some((item) => item.fixed_asset_id === asset.id);
    });

    return (
        <div style={{ padding: '20px', backgroundColor: '#f9f9f9', minHeight: 'calc(100vh - 120px)' }}>
            <h2 style={{ fontSize: '18px', borderBottom: '2px solid #ccc', paddingBottom: '8px', marginBottom: '16px' }}>
                不動産台帳 (R-28)
            </h2>

            {errorStatus && (
                <div style={{ color: 'red', border: '1px solid red', padding: '8px', marginBottom: '12px', backgroundColor: '#fee' }}>
                    {errorStatus}
                </div>
            )}
            {successStatus && (
                <div style={{ color: 'green', border: '1px solid green', padding: '8px', marginBottom: '12px', backgroundColor: '#efe' }}>
                    {successStatus}
                </div>
            )}

            {isEditing ? (
                <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', padding: '16px', borderRadius: '4px' }}>
                    <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{formData.id ? '不動産詳細の編集' : '新規登録'}</h3>
                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>対象固定資産 <span style={{ color: 'red' }}>*</span></label>
                            <select
                                name="fixed_asset_id"
                                value={formData.fixed_asset_id || ''}
                                onChange={handleChange}
                                disabled={!!formData.id} // Cannot change parent asset once created
                                style={{ padding: '4px', maxWidth: '300px' }}
                                required
                            >
                                <option value="">-- 選択してください --</option>
                                {availableAssets.map((asset: any) => (
                                    <option key={asset.id} value={asset.id}>
                                        {asset.asset_code} {asset.asset_name}
                                    </option>
                                ))}
                            </select>

                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>所在地</label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location || ''}
                                onChange={handleChange}
                                style={{ padding: '4px' }}
                            />

                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>地番・家屋番号</label>
                            <input
                                type="text"
                                name="lot_number"
                                value={formData.lot_number || ''}
                                onChange={handleChange}
                                style={{ padding: '4px', maxWidth: '200px' }}
                            />

                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>地目 / 構造</label>
                            <input
                                type="text"
                                name="land_category_structure"
                                value={formData.land_category_structure || ''}
                                onChange={handleChange}
                                style={{ padding: '4px', maxWidth: '200px' }}
                            />

                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>地積 / 床面積 (㎡)</label>
                            <input
                                type="number"
                                step="0.01"
                                name="area"
                                value={formData.area || ''}
                                onChange={handleChange}
                                style={{ padding: '4px', maxWidth: '100px' }}
                            />

                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>登記年月日</label>
                            <input
                                type="date"
                                name="registration_date"
                                value={formData.registration_date || ''}
                                onChange={handleChange}
                                style={{ padding: '4px', maxWidth: '120px' }}
                            />

                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>備考</label>
                            <textarea
                                name="remarks"
                                value={formData.remarks || ''}
                                onChange={handleChange}
                                style={{ padding: '4px', minHeight: '60px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                            <button
                                type="submit"
                                style={{ padding: '6px 16px', backgroundColor: '#0066cc', color: '#fff', border: '1px outset #0066cc', cursor: 'pointer' }}
                            >
                                保存
                            </button>
                            <button
                                type="button"
                                onClick={cancelEdit}
                                style={{ padding: '6px 16px', backgroundColor: '#e0e0e0', border: '1px outset #ccc', cursor: 'pointer' }}
                            >
                                キャンセル
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div style={{ backgroundColor: '#fff', border: '1px solid #ddd', minHeight: '300px' }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #ddd', backgroundColor: '#f0f0f0', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', fontWeight: 'bold', alignSelf: 'center' }}>登録済み不動産一覧</span>
                        <button
                            onClick={handleNew}
                            style={{ padding: '4px 12px', backgroundColor: '#2e7d32', color: '#fff', border: '1px outset #1b5e20', cursor: 'pointer', fontSize: '12px' }}
                        >
                            ＋ 新規追加
                        </button>
                    </div>

                    {loading ? (
                        <div style={{ padding: '16px', fontSize: '12px' }}>読み込み中...</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#e0e0e0', borderBottom: '1px solid #ccc', textAlign: 'left' }}>
                                    <th style={{ padding: '6px', borderRight: '1px solid #ccc' }}>資産コード</th>
                                    <th style={{ padding: '6px', borderRight: '1px solid #ccc' }}>資産名称</th>
                                    <th style={{ padding: '6px', borderRight: '1px solid #ccc' }}>所在地</th>
                                    <th style={{ padding: '6px', borderRight: '1px solid #ccc' }}>地番・家屋番号</th>
                                    <th style={{ padding: '6px', borderRight: '1px solid #ccc' }}>地目/構造</th>
                                    <th style={{ padding: '6px', borderRight: '1px solid #ccc' }}>面積(㎡)</th>
                                    <th style={{ padding: '6px', textAlign: 'center' }}>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                                            登録されている不動産はありません。
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((item) => (
                                        <tr key={item.id} style={{ borderBottom: '1px dotted #ccc' }}>
                                            <td style={{ padding: '6px', borderRight: '1px dotted #eee' }}>{item.asset_code}</td>
                                            <td style={{ padding: '6px', borderRight: '1px dotted #eee' }}>{item.asset_name}</td>
                                            <td style={{ padding: '6px', borderRight: '1px dotted #eee' }}>{item.location || '-'}</td>
                                            <td style={{ padding: '6px', borderRight: '1px dotted #eee' }}>{item.lot_number || '-'}</td>
                                            <td style={{ padding: '6px', borderRight: '1px dotted #eee' }}>{item.land_category_structure || '-'}</td>
                                            <td style={{ padding: '6px', borderRight: '1px dotted #eee', textAlign: 'right' }}>{item.area || '-'}</td>
                                            <td style={{ padding: '6px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    style={{ fontSize: '10px', padding: '2px 8px', marginRight: '4px', cursor: 'pointer' }}
                                                >
                                                    編集
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    style={{ fontSize: '10px', padding: '2px 8px', color: 'red', cursor: 'pointer' }}
                                                >
                                                    削除
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            <div style={{ marginTop: '16px' }}>
                <button
                    onClick={onBack}
                    style={{ padding: '6px 16px', fontSize: '12px', cursor: 'pointer' }}
                >
                    メニューへ戻る
                </button>
            </div>
        </div>
    );
};

export default RealEstateLedger;
